import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabase } from '../supabaseClient';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  
  // États pour les services et catégories
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // États pour les dropdowns
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    service: '',
    category: ''
  });

  // Récupérer les services avec un flag pour éviter les doubles appels
  const servicesLoadedRef = React.useRef(false);
  
  const fetchServices = useCallback(async () => {
    // Éviter les appels multiples
    if (servicesLoadedRef.current) return;
    
    try {
      servicesLoadedRef.current = true;
      
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('services')
        .select('id, name, description')
        .eq('active', true)
        .order('name');
      
      if (error) throw error;
      setServices(data || []);
      
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // console.error('❌ Erreur lors de la récupération des services :', err);
      } else {
        // console.error('❌ Erreur lors de la récupération des services');
      }
      toast.error('Impossible de charger les services');
      // Réinitialiser le flag en cas d'erreur pour permettre une nouvelle tentative
      servicesLoadedRef.current = false;
    }
  }, []);

  // Charger les services au montage du composant
  useEffect(() => {
    fetchServices();
    // Cleanup function pour réinitialiser le flag si le composant est démonté
    return () => {
      servicesLoadedRef.current = false;
    };
  }, [fetchServices]);

  // Récupérer les catégories pour un service donné
  const fetchCategories = useCallback(async (serviceId) => {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, description, service_id')
        .eq('service_id', serviceId)
        .eq('active', true)
        .order('name');
      
      if (error) throw error;
      
      setCategories(data || []);
      
      // Réinitialiser la catégorie sélectionnée
      setFilters(prev => ({
        ...prev,
        category: ''
      }));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // console.error('❌ Erreur lors de la récupération des catégories :', err);
      } else {
        // console.error('❌ Erreur lors de la récupération des catégories');
      }
      toast.error('Impossible de charger les catégories');
    }
  }, []);

  // Fonction pour sélectionner un service
  const handleServiceSelect = (service) => {
    // Charger les catégories pour ce service
    fetchCategories(service.id);
    
    // Mettre à jour les filtres avec le service sélectionné
    setFilters(prev => ({
      ...prev,
      service: service.id,
      category: '' // Réinitialiser la catégorie
    }));
    
    // Fermer le dropdown
    setIsServiceDropdownOpen(false);
  };

  // Handle search input change with debounce for suggestions
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length > 2) {
        try {
          const response = await api.searchConsularActs(searchQuery);
          const data = response.data;
          setSuggestions(data.slice(0, 5));
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            //console.error('Error fetching suggestions:', error);
          } else {
            //console.error('Error fetching suggestions');
          }
        }
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Fonction de recherche
  const handleSearch = () => {
    // Construire l'URL de recherche avec les filtres
    const searchParams = new URLSearchParams();
    
    // Ajouter la requête de recherche si présente
    if (searchQuery) searchParams.set('q', searchQuery);
    
    // Ajouter le service sélectionné
    if (filters.service) searchParams.set('service', filters.service);
    
    // Ajouter la catégorie sélectionnée
    if (filters.category) searchParams.set('category', filters.category);

    // Naviguer vers la page de résultats
    navigate(`/search?${searchParams.toString()}`);
  };

  // Placeholder data for categories if API is not ready
  const placeholderCategories = [
    {
      actId: "1c34d0de-38f7-4bc6-91cc-88000ff61f42",
      name: 'Passeport',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    },
    {
      id: "d04405c5-84a8-4d6e-90dc-40ccdc1eb7ae",
      name: 'État Civil (transcription et autres actes d\'état civil)',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: "1bcac482-dc4d-4ae0-817e-2ce45699a62d",
      name: 'Visa',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      actId: "e94fa9da-ef78-4502-bcc4-4133d4744fb7",
      name: 'CNI',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    },
    {
      actId: "4dbdd60a-9912-415f-8aae-dc59c8ba116b",
      name: 'Immatriculation (Carte consulaire)',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    },
    {
      serviceId: "cd6860f7-6e9f-44a1-b6e3-2d78792715cc",
      name: 'Autres actes consulaires',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    }
  ];

  // Use only placeholder data for categories
  const displayCategories = placeholderCategories;

  // Effet pour gérer la fermeture des dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors du dropdown de service
      const serviceDropdown = document.querySelector('.filter-dropdown:first-child');
      if (
        serviceDropdown && 
        !serviceDropdown.contains(event.target)
      ) {
        setIsServiceDropdownOpen(false);
      }

      // Vérifier si le clic est en dehors du dropdown de catégorie
      const categoryDropdown = document.querySelector('.filter-dropdown:last-child');
      if (
        categoryDropdown && 
        !categoryDropdown.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };

    // Ajouter l'écouteur d'événements au document
    document.addEventListener('mousedown', handleClickOutside);
    
    // Nettoyer l'écouteur d'événements
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full max-w-full overflow-x-hidden min-h-screen">
      {/* Hero Section with Search */}
      <section 
        className="bg-white py-12 md:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden w-full"
        style={{
          backgroundImage: `
            linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,1)),
            repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(200,200,200,0.1) 19px, rgba(200,200,200,0.1) 20px),
            repeating-linear-gradient(90deg, transparent, transparent 19px, rgba(200,200,200,0.1) 19px, rgba(200,200,200,0.1) 20px)
          `,
          backgroundBlendMode: 'multiply'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
          {/*<h1 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{
              background: 'linear-gradient(90deg, #e88c00, #0b8e36)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent'
            }}
          >*/}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-600 mb-6">
            Recherche
          </h1>

          {/* Version mobile */}
          <div className="md:hidden mb-6">
            <div className="flex flex-col space-y-4">
              {/* Champ de recherche mobile */}
              <div className="relative w-full">
                <input 
                  type="text" 
                  placeholder="Rechercher un acte ou un service" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                    {suggestions.map((suggestion, index) => (
                      <div 
                        key={index} 
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => {
                          setSearchQuery(suggestion.label);
                          setSuggestions([]);
                        }}
                      >
                        {suggestion.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Filtres mobiles */}
              <div className="flex flex-col space-y-2">
                <button 
                  onClick={() => {
                    setIsServiceDropdownOpen(!isServiceDropdownOpen);
                    setIsCategoryDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex justify-between items-center"
                >
                  {filters.service 
                    ? services.find(s => s.id === filters.service)?.name 
                    : 'Sélectionner un service'}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isServiceDropdownOpen && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg">
                    {services.map(service => (
                      <div 
                        key={service.id} 
                        onClick={() => handleServiceSelect(service)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {service.name}
                      </div>
                    ))}
                  </div>
                )}

                <button 
                  onClick={() => {
                    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                    setIsServiceDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex justify-between items-center"
                  disabled={!filters.service}
                >
                  {filters.category 
                    ? categories.find(c => c.id === filters.category)?.name 
                    : 'Sélectionner une catégorie'}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isCategoryDropdownOpen && categories.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg">
                    {categories.map(category => (
                      <div 
                        key={category.id} 
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            category: category.id
                          }));
                          setIsCategoryDropdownOpen(false);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {category.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                onClick={handleSearch}
                className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Rechercher
              </button>
            </div>
          </div>

          {/* Search Filters */}
          <div className="hidden md:flex flex-wrap justify-center mb-4 gap-4">
            <div className="filter-dropdown relative w-full md:w-auto">
              <button 
                className="filter-button flex items-center justify-between w-full"
                onClick={() => {
                  setIsServiceDropdownOpen(!isServiceDropdownOpen);
                  setIsCategoryDropdownOpen(false);
                }}
              >
                {filters.service 
                  ? services.find(s => s.id === filters.service)?.name 
                  : 'Service'}
                <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {isServiceDropdownOpen && services.length > 0 && (
                <div className="fixed z-50 mt-1 w-[300px] rounded-md bg-white shadow-lg overflow-y-auto max-h-64">
                  <div className="py-1">
                    {services.map((service) => (
                      <button 
                        key={service.id}
                        className="filter-dropdown-item block w-full text-left px-4 py-3 text-sm hover:bg-gray-100 whitespace-normal break-words"
                        onClick={() => handleServiceSelect(service)}
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="filter-dropdown relative w-full md:w-auto">
              <button 
                className="filter-button flex items-center justify-between w-full"
                onClick={() => {
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                  setIsServiceDropdownOpen(false);
                }}
              >
                {filters.category 
                  ? categories.find(c => c.id === filters.category)?.name 
                  : 'Catégorie'}
                <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {isCategoryDropdownOpen && categories.length > 0 && (
                <div className="fixed z-50 mt-1 w-[300px] rounded-md bg-white shadow-lg overflow-y-auto max-h-64">
                  <div className="py-1">
                    {categories.map((category) => (
                      <button 
                        key={category.id}
                        className="filter-dropdown-item block w-full text-left px-4 py-3 text-sm hover:bg-gray-100 whitespace-normal break-words"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            category: category.id
                          }));
                          setIsCategoryDropdownOpen(false);
                        }}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="search-container hidden md:block">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}>
              <div className="relative flex gap-2">
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary form-input"
                  placeholder="Recherchez un acte, un service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn-modern btn-primary-modern px-6 py-3 rounded-r-md"
                >
                  Rechercher
                </button>
                
                {/* Search Suggestions */}
                {suggestions.length > 0 && (
              <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg border border-gray-200">
                    <ul className="py-1">
                      {suggestions.map((suggestion) => (
                        <li 
                          key={suggestion.id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-left"
                          onClick={() => {
                            navigate(`/actes/${suggestion.id}`);
                            setSuggestions([]);
                          }}
                        >
                          {suggestion.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
      
      {/* Categories Section */}
      <section className="py-12 bg-primary overflow-x-hidden w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 w-full max-w-full">
            {displayCategories.map((category) => (
              <div 
                key={category.id || category.actId || category.serviceId}
                className="category-card w-full max-w-full overflow-hidden"
                onClick={() => {
                  if (category.actId) {
                    navigate(`/actes/${category.actId}`);
                  } else if (category.serviceId) {
                    navigate(`/search?service=${category.serviceId}`);
                  } else {
                    navigate(`/search?category=${category.id}`);
                  }
                }}
              >
                {category.icon || (
                  <svg xmlns="http://www.w3.org/2000/svg" className="category-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <h3 className="text-md font-medium text-gray-900 text-center">{category.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Une question?</h2>
              
              <ul className="space-y-4">
                <li className="text-lg text-gray-700 hover:text-primary cursor-pointer transition-colors">
                  Bonjour! comment puis-je alo alder?
                </li>
                <li className="text-lg text-gray-700 hover:text-primary cursor-pointer transition-colors">
                  Quels documents fournir pour un paspoport?
                </li>
                <li className="text-lg text-gray-700 hover:text-primary cursor-pointer transition-colors">
                  Quel est le coût d&apos;une légalisation?
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-6 space-y-4">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </section> */}
      
      {/* Stats Section */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20">
            <div className="flex items-center gap-4">
              <div className="stat-icon bg-gray-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">10.000 +</p>
                <p className="text-sm text-gray-600">Actes traités par an</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="stat-icon bg-gray-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">Délais</p>
                <p className="text-sm text-gray-600">rapides</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="stat-icon bg-gray-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">Paiement</p>
                <p className="text-sm text-gray-600">sécurisé</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tracking Section */}
      <section className="py-12 bg-gray-50 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <div className="w-full md:w-auto">
              <a href="/paiement-expedition" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Payer les frais d&apos;expédition
              </a>
            </div>
            
            <div className="w-full md:w-auto flex">
              <input
                type="text"
                placeholder="Numéro de demande"
                className="px-4 py-3 border border-gray-300 rounded-l-md focus:ring-primary focus:border-primary flex-1 min-w-[200px] md:min-w-[250px]"
              />
              <button className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-r-md text-white bg-gray-600 hover:bg-gray-700  shadow-sm">
                Consulter
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
